import React, { useState } from 'react';
import { Dialog, Spinner } from '@economic/taco';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from '@lingui/macro';

import InfoDialog from '~/app/shared/components/modals/InfoDialog';
import { useLocalization } from '~/app/shared/components/l10n/l10n';
import { PartialInvoiceDto } from '~/types/internal-api';
import useQuery from '~/app/shared/hooks/useQuery';
import { ErrorDialog } from '~/app/shared/components/modals/ErrorDialog';
import ConvertToInvoice from './ConvertToInvoice';
import CreatePartialInvoice from './CreatePartialInvoice';
import { SalesDocument } from '../../types';

export default function CreatePartialInvoiceDialog() {
    const location = useLocation();
    const navigate = useNavigate();
    const textsLoading = useLocalization(textIds);

    const [showConvertToInvoiceConfirmation, setShowConvertToInvoiceConfirmation] = useState<boolean>(false);

    const salesDocumentId = location.state?.draftId;

    const {
        data: orderData,
        loading: orderQueryLoading,
        error: orderQueryError,
    } = useQuery<SalesDocument>(`sales/order/${salesDocumentId}`);

    const {
        data: partialInvoices,
        loading: partialInvoicesLoading,
        error: partialInvoicesError,
    } = useQuery<PartialInvoiceDto[]>(`sales/partial-invoice/${salesDocumentId}`);

    if (textsLoading || orderQueryLoading || partialInvoicesLoading) {
        return <Spinner />;
    }
    if (orderQueryError !== undefined || partialInvoicesError !== undefined) {
        return <ErrorDialog onClose={close} />;
    }

    return (
        <Dialog
            size={showConvertToInvoiceConfirmation ? 'dialog' : 'sm'}
            defaultOpen
            draggable
            onClose={() => navigate(location.pathname)}>
            <Dialog.Content aria-label={t`Partial invoice`}>
                {({ close }) =>
                    orderData.amount <= 0 ? (
                        <InfoDialog
                            onClose={close}
                            label={t`Cannot create a partial invoice`}
                            message={t`Order amount must be more than 0 to create a partial invoice.`}
                        />
                    ) : showConvertToInvoiceConfirmation ? (
                        <ConvertToInvoice
                            cancel={() => setShowConvertToInvoiceConfirmation(false)}
                            orderId={orderData.salesDocumentId}
                        />
                    ) : (
                        <CreatePartialInvoice
                            close={close}
                            handleShowConvertToInvoiceConfirmation={() => setShowConvertToInvoiceConfirmation(true)}
                            order={orderData}
                            partialInvoices={partialInvoices}
                        />
                    )
                }
            </Dialog.Content>
        </Dialog>
    );
}

const textIds = ['ERROR_CODE_CreatePartialInvoiceProductNotFound'];
